import React, { useEffect, useState } from 'react';
import Styled from './styled';
import {
    ThumbsDownIcon,
    ThumbsUpIcon,
} from '../Icon';
import { IntentFeedback } from 'src/types/enums';
import { Select } from '../index';

export type FeedbackCardProps = {
    /** accessibility support (alt text/aria-label, defaults to 'Thumbs Down') */
    ariaLabelThumbsDown?: string;
    /** accessibility support (alt text/aria-label, defaults to 'Thumbs Up') */
    ariaLabelThumbsUp?: string;
    /** Whether the feedback card buttons are disabled. */
    disabled?: boolean;
    /** Optional header text for the feedback card. */
    header?: string;
    /** Click handler for the thumbs up button */
    thumbsUpClick?: () => void;
    /** Click handler for the thumbs down button */
    thumbsDownClick?: () => void;
    /** Callback for when the user selects a thumbs down reason */
    onThumbsDownResponse?: (reason: string) => void;
    /** Optional feedback rating (if already selected)*/
    rating?: IntentFeedback;
    /** Optional thumbs down reason (if already selected) */
    reason?: string;
    /** Optional thumbs up text (displays on thumbs up selection) */
    thumbsUpText?: string;
    /** Optional thumbs down text (displays on thumbs down selection) */
    thumbsDownText?: string;
    /** Optional thumbs down response text (displays on thumbs down reason selection) */
    thumbsDownResponse?: string;
    /** Label for the "Response Unclear" thumbs down reason */
    RESPONSE_UNCLEAR?: string;
    /** Label for the "Issue Unresolved" thumbs down reason */
    ISSUE_UNRESOLVED?: string;
    /** Label for the "Not Helpful" thumbs down reason */
    NOT_HELPFUL?: string;

};

/**
 * FeedbackCard component that displays a question or prompt with thumbs up and thumbs down icons for feedback.
 */
export const FeedbackCard: React.FC<FeedbackCardProps> = ({
    ariaLabelThumbsDown = IntentFeedback.THUMBS_DOWN,
    ariaLabelThumbsUp = IntentFeedback.THUMBS_UP,
    disabled = false,
    header,
    thumbsUpClick,
    thumbsDownClick,
    onThumbsDownResponse,
    rating,
    reason,
    thumbsUpText,
    thumbsDownText,
    thumbsDownResponse,
    RESPONSE_UNCLEAR = 'Response Unclear',
    ISSUE_UNRESOLVED = 'Did not resolve my issue',
    NOT_HELPFUL = 'Not Helpful',
}) => {
    const [selectedRating, setselectedRating] = useState<IntentFeedback | null>(null);
    const [selectedReason, setSelectedReason] = useState<string | null>(null);
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

    useEffect(() => {
        if (rating) {
            setselectedRating(rating);
        }
    }, [rating]);

    useEffect(() => {
        if (reason) {
            setSelectedReason(reason);
        }
    }, [reason]);

    const reasonLabels: Record<string, string> = {
        RESPONSE_UNCLEAR,
        ISSUE_UNRESOLVED,
        NOT_HELPFUL,
    };

    const feedbackDisabled = disabled || feedbackSubmitted || !!rating || !!reason;

    const handleThumbsUpClick = () => {
        if (!feedbackDisabled) {
            setselectedRating(IntentFeedback.THUMBS_UP);
            if (thumbsUpClick) {
                thumbsUpClick();
                setFeedbackSubmitted(true);
            }
        }
    };

    const handleThumbsDownClick = () => {
        if (!feedbackDisabled) {
            setselectedRating(IntentFeedback.THUMBS_DOWN);
            if (thumbsDownClick) {
                thumbsDownClick();
            }
        }
    };

    const handleReasonSelect = (selectedReason: string) => {
        setSelectedReason(selectedReason);
        if (onThumbsDownResponse) {
            onThumbsDownResponse(selectedReason);
            setFeedbackSubmitted(true);
        }
    };

    return (
        <Styled.FeedbackCard header={header}>
            <Styled.FeedbackWrapper>
                {header && <Styled.Header>{header}</Styled.Header>}
                <Styled.VoteWrapper>
                    <Styled.VoteIcon
                        onClick={handleThumbsUpClick}
                        disabled={feedbackDisabled}
                        aria-label={ariaLabelThumbsUp}
                        tabIndex={0}
                        role='button'
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault(); // Prevent default scrolling for Space key
                                handleThumbsUpClick();
                            }
                        }}
                    >
                        <ThumbsUpIcon selected={selectedRating === IntentFeedback.THUMBS_UP} />
                    </Styled.VoteIcon>
                    <Styled.VoteIcon
                        onClick={handleThumbsDownClick}
                        disabled={feedbackDisabled}
                        aria-label={ariaLabelThumbsDown}
                        tabIndex={0}
                        role='button'
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault(); // Prevent default scrolling for Space key
                                handleThumbsDownClick();
                            }
                        }}
                    >
                        <ThumbsDownIcon selected={selectedRating === IntentFeedback.THUMBS_DOWN} />
                    </Styled.VoteIcon>
                </Styled.VoteWrapper>
            </Styled.FeedbackWrapper>
            {selectedRating && <Styled.Divider />}
            {selectedRating === IntentFeedback.THUMBS_UP && (
                <Styled.Response>{thumbsUpText}</Styled.Response>
            )}

            {selectedRating === IntentFeedback.THUMBS_DOWN && (
                <Styled.ThumbsDownResponse>
                    {thumbsDownText}
                    <Styled.ThumbsDownSelect>
                        <Select
                            disabled={feedbackDisabled}
                            selectedOption={selectedReason}
                            items={
                                selectedReason
                                    ? [
                                        {
                                            label: reasonLabels[selectedReason],
                                            value: selectedReason,
                                            onClick: () => { /* No action needed once reason is confirmed */ },
                                        },
                                    ]
                                    : Object.entries(reasonLabels).map(([key, label]) => ({
                                        label,
                                        value: key,
                                        onClick: () => handleReasonSelect(key),
                                    }))
                            }
                            stretch={true}
                        />
                    </Styled.ThumbsDownSelect>
                    {selectedReason && (
                        <>
                            {thumbsDownResponse}
                        </>
                    )}
                </Styled.ThumbsDownResponse>
            )}
        </Styled.FeedbackCard>
    );
};
